/**
 * Real-Time Feeds List Component
 *
 * Manages real-time data feed subscriptions with support for continuous F&O contracts.
 * Persists feeds to MDMS backend via API.
 *
 * @module pages/Admin/MarketDataManagement/components/RealTimeFeedsList
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSearch, FiPlus, FiFilter, FiRefreshCw, FiCalendar,
  FiTrash2, FiAlertCircle, FiRepeat, FiGlobe, FiClock, FiStar
} from 'react-icons/fi';
import InstrumentSelector from '../../../../components/common/InstrumentSelector';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import { useStateContext } from "../../../../contexts/ContextProvider";
import Loader from "../../../../components/Loader";
import useBreakpoint from '../../../../hooks/useBreakpoint';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import ContinuousContractModal from '../../../../components/common/ContinuousContractModal';
import HQConfigModal from './HQConfigModal';
import { getBrokerDisplay } from '../../../../constants/brokerDisplay';


//--> Status glyph, badges and the capture switch are shared by the desktop table
//    and the phone cards so the two presentations can't drift.
const StatusGlyph = ({ feed }) => (
  <div className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center text-lg font-bold
      ${feed.status === 'active' ? 'bg-profit/10 text-profit' : 'bg-bg-3 text-fg-2'}
  `}>
     {feed.symbol?.[0] || '?'}
  </div>
);

//--> Symbol + the Continuous/HQ markers, with exchange + instrument type beneath.
const InstrumentTitle = ({ feed }) => (
  <div className="min-w-0">
     <div className="font-bold text-fg-1 flex flex-wrap items-center gap-2">
       {feed.symbol}
       {feed.is_continuous && (
         <span className="ds-badge xs info">
           <FiRepeat size={10} /> Continuous
         </span>
       )}
       {feed.is_hq_feed && (
         <span className="ds-badge xs warn">
           <FiStar size={10} /> HQ
         </span>
       )}
     </div>
     <div className="text-xs text-fg-2 flex items-center gap-2">
        <span className="ds-badge xs neutral">
           {feed.exchange}
        </span>
        <span>{feed.instrument_type}</span>
     </div>
  </div>
);

//--> #10505 A6 — broker label + badge tone come from the shared brokerDisplay
//    registry (P5), not a per-component broker-name if-ladder.
const BrokerBadge = ({ feed }) => {
  const { label, tone } = getBrokerDisplay(feed.broker);
  return (
    <span className={`ds-badge sm ${tone}`}>
       {label}
    </span>
  );
};

const SourceBadge = ({ feed }) => (
  <span className={`ds-badge sm ${
     feed.source === 'Manual'
        ? 'role'
        : feed.source === 'Strategy'
        ? 'info'
        : 'warn'
  }`}>
     {feed.source}
  </span>
);

//--> Capture switch. The `after:` pseudo paints a 44x44 touch target around the
//    24px track without occupying layout, so the desktop row height is untouched.
const FeedToggle = ({ checked, onClick, tone = 'accent', title }) => (
  <button
    onClick={onClick}
    title={title}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-bg-1 after:absolute after:content-[''] after:inset-x-0 after:-inset-y-2.5
      ${tone === 'warning'
         ? `focus:ring-warning ${checked ? 'bg-warning' : 'bg-bg-3'}`
         : `focus:ring-accent ${checked ? 'bg-accent' : 'bg-bg-3'}`
      }
    `}
  >
     <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
        ${checked ? 'translate-x-6' : 'translate-x-1'}
     `} />
  </button>
);

//--> HQ broker count. This used to be absolutely positioned on top of the switch
//    next to it: untappable, and ambiguous about which control you were hitting.
//    It now gets its own layout slot, with `after:` painting a 44x44 target so
//    the 20px chip keeps its size.
const HQCountButton = ({ feed, onOpen }) => (
  <button
     onClick={(e) => {
        e.stopPropagation();
        onOpen(feed);
     }}
     className="relative w-5 h-5 shrink-0 bg-bg-2 rounded-full border border-[var(--divider)] flex items-center justify-center text-[10px] font-bold text-fg-2 shadow-sm hover:border-accent hover:text-accent transition-colors after:absolute after:content-[''] after:-inset-3"
     title={`Configure Brokers (Current: ${feed.hq_broker_count || 1})`}
  >
     {feed.hq_broker_count || 1}
  </button>
);


/*>>> MAIN COMPONENT <<<*/
const RealTimeFeedsList = ({ onSelectInstrument }) => {
  const { showSnackbar } = useSnackbar();
  //! Phone gets CARDS, not the table. 9 columns is ~1150px wide, so a 375px
  //! screen only "fits" it by scrolling sideways — which reads as broken/cut-off
  //! and parks the Delete button ~800px off-screen. Desktop keeps the table.
  const { isPhone } = useBreakpoint();

  //! State Management
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pendingInstrument, setPendingInstrument] = useState(null); // For continuous contract modal
  const [hqModalState, setHqModalState] = useState({ isOpen: false, feedId: null, currentCount: 1, symbol: '' });

  // Confirmation Modal State
  const [confirmModalState, setConfirmModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    exchange: '',
    type: '',
    status: ''
  });

  // Timezone display toggle: 'local' or 'exchange'
  const [timeDisplay, setTimeDisplay] = useState('exchange');

  // Exchange timezone mapping (IST for Indian exchanges)
  const EXCHANGE_TIMEZONES = {
    'NSE': 'Asia/Kolkata',
    'BSE': 'Asia/Kolkata',
    'NFO': 'Asia/Kolkata',
    'BFO': 'Asia/Kolkata',
    'MCX': 'Asia/Kolkata',
    'CDS': 'Asia/Kolkata',
    'NCDEX': 'Asia/Kolkata',
    // Add more exchanges as needed
  };

  //--> Fetch feeds from API
  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    try {
      const response = await services.realtimeFeeds.list();
      const data = response.data;
      setFeeds(data.feeds || []);
    } catch (error) {
      console.error('Failed to fetch real-time feeds:', error);
      showSnackbar('Failed to load real-time feeds', 'error');
      setFeeds([]);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  // Initial fetch
  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  //--> Confirmation helpers
  const openConfirmation = (title, message, type, onConfirm) => {
    setConfirmModalState({ isOpen: true, title, message, type, onConfirm });
  };

  const closeConfirmation = () => {
    setConfirmModalState(prev => ({ ...prev, isOpen: false }));
  };

  //--> Handle instrument selection from InstrumentSelector
  const handleInstrumentSelected = (payload) => {
    const instData = payload.instrument || payload;
    // Extract broker from brokerSource if provided
    if (payload.brokerSource) {
      instData.broker = payload.brokerSource.broker_name || payload.brokerSource.broker_id;
    }
    // Open continuous contract modal for F&O, or directly add for others
    setPendingInstrument(instData);
    setIsAddModalOpen(false);
  };

  //--> Handle adding feed after continuous contract modal
  const handleAddFeed = async (config) => {
    const { instrument, is_continuous, rollover_days_before_expiry, active_expiry_date } = config;

    try {
      const payload = {
        instrument_id: instrument.instrument_id,
        symbol: instrument.symbol,
        name: instrument.name,
        exchange: instrument.exchange,
        instrument_type: instrument.instrument_type || 'EQUITY',
        broker: instrument.broker || null,  // Pass selected broker
        source: 'Manual',
        capture_tick: false,
        capture_minute: true,
        is_continuous,
        rollover_days_before_expiry,
        active_expiry_date
      };

      await services.realtimeFeeds.create(payload);
      showSnackbar(`Feed added for ${instrument.symbol}`, 'success');
      fetchFeeds(); // Refresh list
    } catch (error) {
      console.error('Failed to create feed:', error);
      const message = error.response?.data?.detail || 'Failed to add feed';
      showSnackbar(message, 'error');
    } finally {
      setPendingInstrument(null);
    }
  };

  //--> Toggle Handler with API call
  const handleToggle = async (id, field) => {
    const feed = feeds.find(f => f.id === id);
    if (!feed) return;

    const currentValue = field === 'capture_tick' ? feed.capture_tick : feed.capture_minute;
    const action = currentValue ? 'Disable' : 'Enable';
    const type = field === 'capture_tick' ? 'Tick-by-Tick' : '1-Minute';
    const isDestructive = currentValue;

    openConfirmation(
      `${action} ${type} Capture?`,
      `Are you sure you want to ${action.toLowerCase()} ${type.toLowerCase()} data capture for ${feed.symbol}?`,
      isDestructive ? 'warning' : 'info',
      async () => {
        try {
          if (field === 'capture_tick') {
            await services.realtimeFeeds.toggleTick(id);
          } else {
            await services.realtimeFeeds.toggleMinute(id);
          }
          // Optimistic update
          setFeeds(prev => prev.map(f => {
            if (f.id === id) {
              return { ...f, [field]: !currentValue };
            }
            return f;
          }));
          showSnackbar(`${type} capture ${action.toLowerCase()}d for ${feed.symbol}`, 'success');
        } catch (error) {
          console.error('Failed to toggle:', error);
          showSnackbar('Failed to update capture setting', 'error');
        }
      }
    );
  };

  //--> Toggle HQ Handler with API call
  const handleToggleHQ = async (id) => {
    const feed = feeds.find(f => f.id === id);
    if (!feed) return;

    const currentValue = feed.is_hq_feed;
    const action = currentValue ? 'Disable' : 'Enable';
    const isDestructive = currentValue;

    openConfirmation(
      `${action} HQ Multi-Broker Mode?`,
      `Are you sure you want to ${action.toLowerCase()} HQ (multi-broker) mode for ${feed.symbol}? ${currentValue ? 'This will reduce to single broker.' : 'This will subscribe via 3 brokers for higher quality data.'}`,
      isDestructive ? 'warning' : 'info',
      async () => {
        try {
          await services.realtimeFeeds.toggleHQ(id);
          // Optimistic update
          setFeeds(prev => prev.map(f => {
            if (f.id === id) {
              return { ...f, is_hq_feed: !currentValue };
            }
            return f;
          }));
          showSnackbar(`HQ mode ${action.toLowerCase()}d for ${feed.symbol}`, 'success');
        } catch (error) {
          console.error('Failed to toggle HQ:', error);
          showSnackbar('Failed to update HQ setting', 'error');
        }
      }
    );
  };

  //--> Open the HQ broker-count modal for a feed
  const openHQConfig = (feed) => {
    setHqModalState({
      isOpen: true,
      feedId: feed.id,
      currentCount: feed.hq_broker_count || 1,
      symbol: feed.symbol
    });
  };

  //--> Handle HQ Config Update
  const handleHQConfigUpdate = async (newCount) => {
    const { feedId, symbol } = hqModalState;
    if (!feedId) return;

    try {
      await services.realtimeFeeds.update(feedId, { hq_broker_count: newCount });

      setFeeds(prev => prev.map(f => {
        if (f.id === feedId) {
          return { ...f, hq_broker_count: newCount };
        }
        return f;
      }));

      showSnackbar(`Updated HQ configuration for ${symbol} to ${newCount} brokers`, 'success');
      setHqModalState(prev => ({ ...prev, isOpen: false }));
    } catch (error) {
       console.error('Failed to update HQ config:', error);
       showSnackbar('Failed to update configuration', 'error');
    }
  };

  //--> Delete Handler with API call
  const handleDelete = (id, e) => {
    e.stopPropagation();
    const feed = feeds.find(f => f.id === id);
    if (!feed) return;

    openConfirmation(
      'Stop Feed?',
      `Are you sure you want to stop and remove the real-time feed for ${feed.symbol}? This will stop data collection immediately.`,
      'danger',
      async () => {
        try {
          await services.realtimeFeeds.delete(id);
          setFeeds(prev => prev.filter(f => f.id !== id));
          showSnackbar(`Feed removed for ${feed.symbol}`, 'success');
        } catch (error) {
          console.error('Failed to delete feed:', error);
          showSnackbar('Failed to remove feed', 'error');
        }
      }
    );
  };

  //--> Filter Logic
  const uniqueExchanges = [...new Set(feeds.map(f => f.exchange))];
  const uniqueTypes = [...new Set(feeds.map(f => f.instrument_type))];

  const filteredFeeds = feeds.filter(feed => {
    const matchesSearch = feed.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          feed.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesExchange = filters.exchange ? feed.exchange === filters.exchange : true;
    const matchesType = filters.type ? feed.instrument_type === filters.type : true;
    const matchesStatus = filters.status ? feed.status === filters.status : true;

    return matchesSearch && matchesExchange && matchesType && matchesStatus;
  });

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  //--> Format date helper with timezone support
  const formatDate = (dateStr, exchange = null) => {
    if (!dateStr) return '—';

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '—';

      // Determine timezone to use
      let options = {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      };

      if (timeDisplay === 'exchange' && exchange && EXCHANGE_TIMEZONES[exchange]) {
        options.timeZone = EXCHANGE_TIMEZONES[exchange];
      }

      return date.toLocaleDateString('en-IN', options);
    } catch {
      return '—';
    }
  };

  // Get timezone label for display
  const getTimezoneLabel = (exchange) => {
    if (timeDisplay === 'local') return 'Local';
    if (exchange && EXCHANGE_TIMEZONES[exchange]) {
      // Return short timezone code
      return exchange === 'MCX' || exchange.includes('N') || exchange.includes('B') ? 'IST' : 'Exch';
    }
    return 'Local';
  };

  //--> Shared empty-state copy for both presentations.
  const emptyMessage = activeFilterCount > 0 || searchQuery
    ? 'No active feeds found matching your filters.'
    : 'No active feeds. Click "Add Feed" to get started.';

  //--> Shared expiry/rollover value (date + its icon, or an em-dash).
  const renderFeedDate = (dateStr, exchange, Icon, iconClass) => (
    dateStr ? (
      <span className="text-sm text-fg-2 flex items-center gap-1">
        <Icon size={14} className={iconClass} />
        {formatDate(dateStr, exchange)}
      </span>
    ) : (
      <span className="text-sm text-fg-3">—</span>
    )
  );

  //--> PHONE: one card per feed. Every toggle is a labelled full-width row and
  //    both actions are real buttons on the card, so nothing lives in a column
  //    you have to pan sideways to reach.
  const renderFeedCards = () => (
    <div className="flex flex-col gap-3">
      {filteredFeeds.length === 0 ? (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2 px-4 py-12 text-center text-fg-3">
          {emptyMessage}
        </div>
      ) : (
        filteredFeeds.map(feed => (
          <div key={feed.id} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
            {/* Instrument */}
            <div className="flex items-center gap-3">
              <StatusGlyph feed={feed} />
              <InstrumentTitle feed={feed} />
            </div>

            {/* Broker + Source */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <BrokerBadge feed={feed} />
              <SourceBadge feed={feed} />
            </div>

            {/* Capture toggles — label left, switch right */}
            <div className="mt-2 border-t border-[var(--divider)] pt-1">
              <div className="flex min-h-[44px] items-center justify-between gap-3">
                <span className="text-sm text-fg-2">Tick Data</span>
                <FeedToggle
                  checked={feed.capture_tick}
                  onClick={() => handleToggle(feed.id, 'capture_tick')}
                />
              </div>
              <div className="flex min-h-[44px] items-center justify-between gap-3">
                <span className="text-sm text-fg-2">1m Data</span>
                <FeedToggle
                  checked={feed.capture_minute}
                  onClick={() => handleToggle(feed.id, 'capture_minute')}
                />
              </div>
              <div className="flex min-h-[44px] items-center justify-between gap-3">
                <span className="text-sm text-fg-2">HQ Multi-Broker</span>
                <div className="flex items-center gap-3">
                  {feed.is_hq_feed && <HQCountButton feed={feed} onOpen={openHQConfig} />}
                  <FeedToggle
                    checked={feed.is_hq_feed}
                    onClick={() => handleToggleHQ(feed.id)}
                    tone="warning"
                    title={feed.is_hq_feed ? 'HQ Mode: Streaming via 3 brokers' : 'Standard Mode: Single broker'}
                  />
                </div>
              </div>
            </div>

            {/* Expiry + Rollover */}
            <div className="grid grid-cols-2 gap-3 border-t border-[var(--divider)] pt-3">
              <div>
                <span className="mb-1 block text-xs font-semibold text-fg-3 uppercase">Expiry</span>
                {renderFeedDate(feed.active_expiry_date, feed.exchange, FiCalendar, 'text-warning')}
              </div>
              <div>
                <span className="mb-1 block text-xs font-semibold text-fg-3 uppercase">Rollover</span>
                {renderFeedDate(feed.next_rollover_date, feed.exchange, FiRepeat, 'text-accent')}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                 type="button"
                 onClick={() => onSelectInstrument(feed)}
                 className="btn btn-secondary flex items-center justify-center gap-2"
              >
                 <FiCalendar size={16} /> View Timeline
              </button>
              <button
                 type="button"
                 onClick={(e) => handleDelete(feed.id, e)}
                 className="btn btn-danger flex items-center justify-center gap-2"
              >
                 <FiTrash2 size={16} /> Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  //--> DESKTOP: the full 9-column table.
  const renderFeedTable = () => (
    <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] overflow-hidden">
       <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/20">
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Instrument</th>
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Broker</th>
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Source</th>
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider text-center">Tick Data</th>
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider text-center">1m Data</th>
                  <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider text-center">HQ</th>
                  <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Expiry</th>
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider">Rollover</th>
                 <th className="py-4 px-6 text-xs font-semibold text-fg-2 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
               {filteredFeeds.length === 0 ? (
                   <tr>
                     <td colSpan="9" className="py-12 text-center text-fg-3">
                        {emptyMessage}
                     </td>
                   </tr>
               ) : (
                   filteredFeeds.map(feed => (
                      <tr
                        key={feed.id}
                        onClick={() => onSelectInstrument(feed)}
                        className="group hover:bg-bg-3/30 transition-colors cursor-pointer"
                      >
                        {/* Instrument */}
                        <td className="py-4 px-6">
                           <div className="flex items-center gap-3">
                              <StatusGlyph feed={feed} />
                              <InstrumentTitle feed={feed} />
                           </div>
                        </td>

                        {/* Broker */}
                        <td className="py-4 px-6">
                           <BrokerBadge feed={feed} />
                        </td>

                        {/* Source */}
                        <td className="py-4 px-6">
                           <SourceBadge feed={feed} />
                        </td>

                        {/* Tick Data Toggle */}
                        <td className="py-4 px-6 text-center" onClick={e => e.stopPropagation()}>
                            <FeedToggle
                              checked={feed.capture_tick}
                              onClick={() => handleToggle(feed.id, 'capture_tick')}
                            />
                        </td>

                        {/* 1m Data Toggle */}
                        <td className="py-4 px-6 text-center" onClick={e => e.stopPropagation()}>
                            <FeedToggle
                              checked={feed.capture_minute}
                              onClick={() => handleToggle(feed.id, 'capture_minute')}
                            />
                        </td>

                         {/* HQ Mode Toggle */}
                         <td className="py-4 px-6" onClick={e => e.stopPropagation()}>
                             <div className="flex items-center justify-center gap-3">
                                <FeedToggle
                                  checked={feed.is_hq_feed}
                                  onClick={() => handleToggleHQ(feed.id)}
                                  tone="warning"
                                  title={feed.is_hq_feed ? 'HQ Mode: Streaming via 3 brokers' : 'Standard Mode: Single broker'}
                                />
                                {feed.is_hq_feed && <HQCountButton feed={feed} onOpen={openHQConfig} />}
                             </div>
                          </td>

                         {/* Expiry Date */}
                        <td className="py-4 px-6">
                           {renderFeedDate(feed.active_expiry_date, feed.exchange, FiCalendar, 'text-warning')}
                        </td>

                        {/* Rollover Date */}
                        <td className="py-4 px-6">
                           {renderFeedDate(feed.next_rollover_date, feed.exchange, FiRepeat, 'text-accent')}
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-6 text-right">
                           <div className="flex items-center justify-end gap-3" onClick={e => e.stopPropagation()}>
                              <button
                                 className="relative p-2 text-fg-3 hover:text-accent hover:bg-accent/10 rounded-lg transition-colors after:absolute after:content-[''] after:-inset-[5px]"
                                 title="View Timeline"
                                 onClick={() => onSelectInstrument(feed)}
                              >
                                 <FiCalendar size={18} />
                              </button>
                              <button
                                 onClick={(e) => handleDelete(feed.id, e)}
                                 className="relative p-2 text-fg-3 hover:text-loss hover:bg-loss/10 rounded-lg transition-colors after:absolute after:content-[''] after:-inset-[5px]"
                                 title="Stop & Remove"
                              >
                                 <FiTrash2 size={18} />
                              </button>
                           </div>
                        </td>
                      </tr>
                   ))
               )}
            </tbody>
          </table>
       </div>
    </div>
  );

  return (
    <div className="space-y-4">
       {/* Toolbar */}
       <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-bg-2 p-4 rounded-xl shadow-sm border border-[var(--divider)]">
         {/* Search */}
         <div className="relative w-full md:w-96 group">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-3 group-focus-within:text-accent transition-colors" />
            <input
              type="text"
              placeholder="Search active feeds..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full ds-input !pl-10"
            />
         </div>

         {/* Actions */}
         <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={fetchFeeds}
              disabled={loading}
              className="p-2.5 border border-[var(--divider)] rounded-lg text-fg-2 hover:bg-bg-3 transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            </button>

            {/* Timezone Toggle */}
            <button
              onClick={() => setTimeDisplay(prev => prev === 'local' ? 'exchange' : 'local')}
              className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg transition-colors font-medium text-sm
                  ${timeDisplay === 'exchange'
                      ? 'bg-accent/10 border-accent/30 text-accent'
                      : 'bg-bg-2 border-[var(--divider)] text-fg-2 hover:bg-bg-3'
                  }
              `}
              title={timeDisplay === 'local' ? 'Showing Local Time (click for Exchange Time)' : 'Showing Exchange Time (click for Local Time)'}
            >
               {timeDisplay === 'local' ? <FiClock size={16} /> : <FiGlobe size={16} />}
               <span className="hidden sm:inline">{timeDisplay === 'local' ? 'Local' : 'Exchange'}</span>
            </button>

            <button
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors font-medium text-sm relative
                    ${isFilterPanelOpen || activeFilterCount > 0
                        ? 'bg-accent/10 border-accent/30 text-accent'
                        : 'bg-bg-2 border-[var(--divider)] text-fg-2 hover:bg-bg-3'
                    }
                `}
            >
               <FiFilter className={isFilterPanelOpen || activeFilterCount > 0 ? 'text-accent' : 'text-fg-2'} />
               Filters
               {activeFilterCount > 0 && (
                 <span className="absolute -top-2 -right-2 w-5 h-5 bg-accent text-white text-xs rounded-full flex items-center justify-center">
                   {activeFilterCount}
                 </span>
               )}
            </button>
            <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="btn btn-primary btn-sm flex items-center gap-2 flex-1 md:flex-none justify-center"
            >
               <FiPlus size={18} /> Add Feed
            </button>
         </div>
       </div>

       {/* Filter Panel */}
       {isFilterPanelOpen && (
         <div className="bg-bg-3/50 p-4 rounded-xl border border-[var(--divider)] grid grid-cols-1 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Exchange Filter */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-fg-2 uppercase">Exchange</label>
              <select
                value={filters.exchange}
                onChange={e => setFilters(prev => ({...prev, exchange: e.target.value}))}
                className="w-full ds-input"
              >
                <option value="">All Exchanges</option>
                {uniqueExchanges.map(ex => <option key={ex} value={ex}>{ex}</option>)}
              </select>
            </div>

            {/* Type Filter */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-fg-2 uppercase">Instrument Type</label>
              <select
                value={filters.type}
                onChange={e => setFilters(prev => ({...prev, type: e.target.value}))}
                className="w-full ds-input"
              >
                <option value="">All Types</option>
                {uniqueTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Status Filter */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-fg-2 uppercase">Feed Status</label>
              <select
                value={filters.status}
                onChange={e => setFilters(prev => ({...prev, status: e.target.value}))}
                className="w-full ds-input"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="error">Error</option>
              </select>
            </div>

            {/* Clear Button */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({ exchange: '', type: '', status: '' });
                  setSearchQuery('');
                }}
                disabled={activeFilterCount === 0 && !searchQuery}
                className="w-full px-4 py-2 bg-bg-2 border border-[var(--divider)] text-fg-2 rounded-lg hover:bg-loss/10 hover:text-loss hover:border-loss/30 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear All Filters
              </button>
            </div>
         </div>
       )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center p-8">
           <Loader text="Loading feeds..." />
        </div>
      )}

       {/* Feeds — cards on a phone, table on desktop */}
       {!loading && (isPhone ? renderFeedCards() : renderFeedTable())}

       {/* Add Instrument Modal */}
       <InstrumentSelector
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSelect={handleInstrumentSelected}
          title="Add Real-time Feed"
          requireBrokerSelection={true}
       />

       {/* Continuous Contract Modal */}
       <ContinuousContractModal
         isOpen={!!pendingInstrument}
         onClose={() => setPendingInstrument(null)}
         onConfirm={handleAddFeed}
         instrument={pendingInstrument}
       />

       {/* Custom Confirmation Modal */}
       <ConfirmationModal
          isOpen={confirmModalState.isOpen}
          onClose={closeConfirmation}
          onConfirm={confirmModalState.onConfirm}
          title={confirmModalState.title}
          message={confirmModalState.message}
          type={confirmModalState.type}
          confirmText={confirmModalState.type === 'danger' ? 'Yes, Stop Feed' : 'Confirm'}
       />

       {/* HQ Configuration Modal */}
       <HQConfigModal
          isOpen={hqModalState.isOpen}
          onClose={() => setHqModalState(prev => ({ ...prev, isOpen: false }))}
          onConfirm={handleHQConfigUpdate}
          currentCount={hqModalState.currentCount}
          symbol={hqModalState.symbol}
       />
    </div>
  );
};

export default RealTimeFeedsList;
